import { useAuth } from '@/contexts/AuthContext';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Input } from '@/components/ui/input';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, Star, Mail, ShoppingCart } from 'lucide-react';

export function DashboardHeader({ title = 'Dashboard' }: { title?: string }) {
  const { profile, user, roles } = useAuth();
  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'User';
  const roleLabel = roles.includes('superadmin') ? 'Super Admin'
    : roles.includes('developer') ? 'Developer'
    : roles.includes('agent') ? 'Agent'
    : 'Member';

  return (
    <header className="h-16 flex items-center justify-between border-b px-4 md:px-6 bg-card/80 backdrop-blur-sm sticky top-0 z-40">
      <div className="flex items-center gap-3">
        <SidebarTrigger className="mr-1" />
        <h1 className="font-display font-bold text-lg hidden sm:block">{title}</h1>
      </div>

      <div className="hidden md:flex items-center relative max-w-xs w-full mx-4">
        <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search here..." className="pl-9 bg-muted/50 border-0 h-9 rounded-lg" />
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        <button className="relative p-2 rounded-lg hover:bg-muted/50 text-muted-foreground">
          <Star className="h-4 w-4" />
          <span className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center">38</span>
        </button>
        <NotificationBell />
        <button className="relative p-2 rounded-lg hover:bg-muted/50 text-muted-foreground">
          <Mail className="h-4 w-4" />
          <span className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center">!</span>
        </button>
        <button className="relative p-2 rounded-lg hover:bg-muted/50 text-muted-foreground">
          <ShoppingCart className="h-4 w-4" />
          <span className="absolute -top-0.5 -right-0.5 bg-accent text-accent-foreground text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center">67</span>
        </button>

        <div className="flex items-center gap-2 ml-2 pl-2 border-l">
          <Avatar className="h-9 w-9">
            <AvatarImage src={profile?.avatar_url} />
            <AvatarFallback className="bg-muted text-xs">{displayName[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="hidden lg:block text-right">
            <div className="text-sm font-semibold leading-tight">{displayName}</div>
            <div className="text-[10px] text-muted-foreground">{roleLabel}</div>
          </div>
        </div>
      </div>
    </header>
  );
}
